import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, RotateCcw, Info } from 'lucide-react';
interface Slide1Props {
  isDark?: boolean;
}
const DEFAULT_LEADERS = 25;
const DEFAULT_COST_PER_LEADER = 150000;
export function Slide1({
  isDark = false
}: Slide1Props) {
  const [peopleLeaders, setPeopleLeaders] = useState(DEFAULT_LEADERS);
  const [costPerLeader, setCostPerLeader] = useState(DEFAULT_COST_PER_LEADER);
  const [showTooltip5, setShowTooltip5] = useState(false);
  const [showTooltip10, setShowTooltip10] = useState(false);
  const [showTooltipSource, setShowTooltipSource] = useState(false);
  const totalInvestment = peopleLeaders * costPerLeader;
  const efficiency5 = totalInvestment * 0.05;
  const efficiency10 = totalInvestment * 0.1;
  const handleReset = () => {
    setPeopleLeaders(DEFAULT_LEADERS);
    setCostPerLeader(DEFAULT_COST_PER_LEADER);
  };
  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    }
    return `$${(value / 1000).toFixed(0)}K`;
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <motion.div className="h-full w-full overflow-y-auto overflow-x-hidden" variants={containerVariants} initial="hidden" animate="visible" data-id="element-733">
      <div className="min-h-full flex flex-col justify-start md:justify-center max-w-6xl mx-auto px-6 sm:px-12 py-20 sm:py-24" data-id="element-734">
        <motion.div variants={itemVariants} className="mb-12 sm:mb-16" data-id="element-735">
          <div className="flex items-start gap-2 sm:gap-3 mb-5 sm:mb-6" data-id="element-736">
            <h1 className={`text-3xl sm:text-5xl font-serif font-medium tracking-tight ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-737">
              The Economic Case for Heliona
            </h1>
            <div className="relative" onMouseEnter={() => setShowTooltipSource(true)} onMouseLeave={() => setShowTooltipSource(false)} data-id="element-738">
              <button className={`inline-flex items-center justify-center p-1.5 sm:p-2 rounded-full transition-colors mt-1 ${isDark ? 'hover:bg-white/10 text-white/50 hover:text-white/70' : 'hover:bg-heliona-charcoal/10 text-heliona-charcoal/50 hover:text-heliona-charcoal/70'}`} aria-label="View source information" data-id="element-739">
                <Info className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-740" />
              </button>
              <AnimatePresence data-id="element-741">
                {showTooltipSource && <motion.div initial={{
                opacity: 0,
                y: -5
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -5
              }} transition={{
                duration: 0.2
              }} className={`absolute left-0 top-full mt-2 p-4 rounded-xl shadow-xl text-xs sm:text-sm w-80 max-w-[calc(100vw-3rem)] z-50 backdrop-blur-sm ${isDark ? 'bg-heliona-navy/95 border border-white/20 text-white' : 'bg-white/95 border border-heliona-charcoal/20 text-heliona-charcoal'}`} data-id="element-742">
                    <p className="font-semibold mb-2" data-id="element-743">Source:</p>
                    <p className="mb-3 leading-relaxed" data-id="element-744">
                      Economic data based on research published in the{' '}
                      <span className="font-medium" data-id="element-745">
                        American Journal of Preventive Medicine
                      </span>
                    </p>
                    <a href="https://www.ajpmonline.org/article/S0749-3797(25)00023-6/abstract" target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 text-xs font-medium underline ${isDark ? 'text-heliona-mint hover:text-heliona-mint-light' : 'text-heliona-mint-dark hover:text-heliona-mint'}`} onClick={e => e.stopPropagation()} data-id="element-746">
                      View full article →
                    </a>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
          <p className={`text-base sm:text-xl max-w-3xl leading-relaxed ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-747">
            Most people leaders were promoted for performance, not trained for
            people leadership. Even small efficiency gains create measurable
            ROI.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 sm:gap-16 items-start" data-id="element-748">
          {/* Company Profile Card */}
          <motion.div variants={itemVariants} className={`rounded-3xl p-6 sm:p-10 shadow-lg ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-gradient-to-br from-heliona-navy/10 to-heliona-slate/5 border border-heliona-navy/20'}`} data-id="element-749">
            <div className="flex items-center justify-between mb-6 sm:mb-8" data-id="element-750">
              <h3 className={`text-lg sm:text-2xl font-semibold ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-751">
                Example Company
              </h3>
              <button onClick={handleReset} className={`p-2 rounded-lg transition-all ${isDark ? 'bg-white/5 hover:bg-white/10 text-white/70 hover:text-white' : 'bg-heliona-charcoal/5 hover:bg-heliona-charcoal/10 text-heliona-charcoal/70 hover:text-heliona-charcoal'}`} aria-label="Reset to defaults" data-id="element-752">
                <RotateCcw className="w-4 h-4" data-id="element-753" />
              </button>
            </div>

            <div className="space-y-5 sm:space-y-7" data-id="element-754">
              {/* People Leaders Input */}
              <div className={`pb-4 sm:pb-6 border-b ${isDark ? 'border-white/10' : 'border-heliona-navy/20'}`} data-id="element-755">
                <div className="flex justify-between items-end gap-3" data-id="element-756">
                  <span className={`text-sm sm:text-lg font-medium ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-757">
                    People Leaders
                  </span>
                  <input type="number" value={peopleLeaders} onChange={e => setPeopleLeaders(Math.max(1, parseInt(e.target.value) || 1))} className={`text-2xl sm:text-4xl font-bold text-right w-24 sm:w-32 bg-transparent border-b-2 transition-colors focus:outline-none ${isDark ? 'text-white border-white/20 focus:border-heliona-mint' : 'text-heliona-charcoal border-heliona-navy/20 focus:border-heliona-mint-dark'}`} data-id="element-758" />
                </div>
              </div>

              {/* Cost Per Leader Input */}
              <div className={`pb-4 sm:pb-6 border-b ${isDark ? 'border-white/10' : 'border-heliona-navy/20'}`} data-id="element-759">
                <div className="flex justify-between items-end gap-3" data-id="element-760">
                  <div data-id="element-761">
                    <span className={`block text-sm sm:text-lg font-medium ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-762">
                      Avg. fully loaded Cost per Leader
                    </span>
                    <span className={`text-xs sm:text-sm block mt-1.5 ${isDark ? 'text-white/60' : 'text-heliona-text-gray/70'}`} data-id="element-763">
                      (salary + benefits + overhead)
                    </span>
                  </div>
                  <div className="flex items-baseline" data-id="element-764">
                    <span className={`text-xl sm:text-3xl font-bold mr-1 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-765">
                      $
                    </span>
                    <input type="number" value={costPerLeader / 1000} onChange={e => setCostPerLeader(Math.max(1, parseInt(e.target.value) || 1) * 1000)} className={`text-xl sm:text-3xl font-bold text-right w-20 sm:w-24 bg-transparent border-b-2 transition-colors focus:outline-none ${isDark ? 'text-white border-white/20 focus:border-heliona-mint' : 'text-heliona-charcoal border-heliona-navy/20 focus:border-heliona-mint-dark'}`} data-id="element-766" />
                    <span className={`text-xl sm:text-3xl font-bold ml-1 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-767">
                      K
                    </span>
                  </div>
                </div>
              </div>

              {/* Total Investment */}
              <div className="flex justify-between items-end pt-2" data-id="element-768">
                <span className={`text-sm sm:text-lg font-medium ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-769">
                  Total Investment
                </span>
                <AnimatePresence mode="wait" data-id="element-770">
                  <motion.span key={totalInvestment} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.3
                }} className={`text-3xl sm:text-5xl font-bold ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-771">
                    {formatCurrency(totalInvestment)}
                  </motion.span>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* Value Visualization */}
          <motion.div variants={itemVariants} className="space-y-6 sm:space-y-10 mb-16 sm:mb-0" data-id="element-772">
            <div data-id="element-773">
              <p className={`font-semibold mb-4 sm:mb-5 text-base sm:text-lg ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-774">
                Conservative assumptions:
              </p>
              <ul className={`space-y-3 sm:space-y-4 text-sm sm:text-base leading-relaxed ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-775">
                <li className="flex items-start gap-3" data-id="element-776">
                  <span className={`mt-1 flex-shrink-0 text-lg ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-777">
                    •
                  </span>
                  <span data-id="element-778">
                    Burnout, poor communication, and leadership friction waste
                    at least 5–10% of leadership time
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-779">
                  <span className={`mt-1 flex-shrink-0 text-lg ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-780">
                    •
                  </span>
                  <span data-id="element-781">
                    That is time spent on rework, conflict, disengagement,
                    turnover, and bad decisions
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-782">
                  <span className={`mt-1 flex-shrink-0 text-lg ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-783">
                    •
                  </span>
                  <span data-id="element-784">Even HR leaders will agree this is conservative</span>
                </li>
              </ul>
            </div>

            <div className="space-y-6 sm:space-y-8" data-id="element-785">
              {/* 5% Efficiency Gain */}
              <div data-id="element-786">
                <div className="flex justify-between items-baseline mb-3 sm:mb-4" data-id="element-787">
                  <div className="relative flex items-center gap-2" data-id="element-788">
                    <span className={`font-semibold text-sm sm:text-lg ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-789">
                      5% Efficiency Gain
                    </span>
                    <button onMouseEnter={() => setShowTooltip5(true)} onMouseLeave={() => setShowTooltip5(false)} className={`p-1 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/50' : 'hover:bg-heliona-charcoal/10 text-heliona-charcoal/50'}`} data-id="element-790">
                      <Info className="w-3.5 h-3.5" data-id="element-791" />
                    </button>
                    <AnimatePresence data-id="element-792">
                      {showTooltip5 && <motion.div initial={{
                      opacity: 0,
                      y: 5
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: 5
                    }} className={`absolute left-0 top-full mt-2 p-3 rounded-lg shadow-lg text-xs max-w-xs z-50 ${isDark ? 'bg-heliona-navy/95 border border-white/20 text-white' : 'bg-white border border-heliona-charcoal/20 text-heliona-charcoal'}`} data-id="element-793">
                          5% of total investment recovered through reduced
                          burnout, improved communication, and better
                          decision-making
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                  <AnimatePresence mode="wait" data-id="element-794">
                    <motion.span key={efficiency5} initial={{
                    opacity: 0,
                    scale: 0.8
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.8
                  }} transition={{
                    duration: 0.3
                  }} className="text-heliona-orange font-bold text-xl sm:text-3xl" data-id="element-795">
                      {formatCurrency(efficiency5)}
                    </motion.span>
                  </AnimatePresence>
                </div>
                <div className={`h-3 sm:h-4 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-heliona-beige'}`} data-id="element-796">
                  <motion.div key={`bar5-${efficiency5}`} initial={{
                  width: 0
                }} animate={{
                  width: '50%'
                }} transition={{
                  duration: 1.2,
                  ease: [0.25, 0.1, 0.25, 1]
                }} className="h-full bg-gradient-to-r from-heliona-orange to-amber-500 rounded-full shadow-sm" data-id="element-797" />
                </div>
              </div>

              {/* 10% Efficiency Gain */}
              <div data-id="element-798">
                <div className="flex justify-between items-baseline mb-3 sm:mb-4" data-id="element-799">
                  <div className="relative flex items-center gap-2" data-id="element-800">
                    <span className={`font-semibold text-sm sm:text-lg ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-801">
                      10% Efficiency Gain
                    </span>
                    <button onMouseEnter={() => setShowTooltip10(true)} onMouseLeave={() => setShowTooltip10(false)} className={`p-1 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/50' : 'hover:bg-heliona-charcoal/10 text-heliona-charcoal/50'}`} data-id="element-802">
                      <Info className="w-3.5 h-3.5" data-id="element-803" />
                    </button>
                    <AnimatePresence data-id="element-804">
                      {showTooltip10 && <motion.div initial={{
                      opacity: 0,
                      y: 5
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: 5
                    }} className={`absolute left-0 top-full mt-2 p-3 rounded-lg shadow-lg text-xs max-w-xs z-50 ${isDark ? 'bg-heliona-navy/95 border border-white/20 text-white' : 'bg-white border border-heliona-charcoal/20 text-heliona-charcoal'}`} data-id="element-805">
                          10% of total investment recovered through
                          comprehensive leadership development and sustained
                          organizational health
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                  <AnimatePresence mode="wait" data-id="element-806">
                    <motion.span key={efficiency10} initial={{
                    opacity: 0,
                    scale: 0.8
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.8
                  }} transition={{
                    duration: 0.3
                  }} className={`font-bold text-xl sm:text-3xl ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-807">
                      {formatCurrency(efficiency10)}
                    </motion.span>
                  </AnimatePresence>
                </div>
                <div className={`h-3 sm:h-4 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-heliona-beige'}`} data-id="element-808">
                  <motion.div key={`bar10-${efficiency10}`} initial={{
                  width: 0
                }} animate={{
                  width: '100%'
                }} transition={{
                  duration: 1.2,
                  ease: [0.25, 0.1, 0.25, 1]
                }} className="h-full bg-gradient-to-r from-heliona-mint to-heliona-mint-dark rounded-full shadow-sm" data-id="element-809" />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 sm:gap-5 pt-4 sm:pt-6" data-id="element-810">
              <div className="p-2.5 sm:p-3 rounded-xl bg-heliona-mint/10 border border-heliona-mint/30" data-id="element-811">
                <TrendingUp className={`w-6 h-6 sm:w-8 sm:h-8 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-812" />
              </div>
              <p className={`text-sm sm:text-xl font-medium ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-813">
                Modest improvements generate strong ROI
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>;
}